/**
 * AdminNavButtons — Primera fila de navegación admin
 *
 * Botones: Equipo, Usuarios, Precios, Finanzas,
 *          Presupuestos, Dossier, Plantillas.
 *
 * Solo visible cuando user.role === 'admin' && !isLiveMode.
 * Extraído de App.js líneas 6121-6208 durante la Ola B del refactor 2026-04-20.
 */

import { Button } from './ui/button';
import { prefetchHandlers } from '../utils/lazyPrefetch';
import { UsersRound, Users, DollarSign, TrendingUp, FileText, Settings, ShieldAlert, HardDrive, MapPinned, Gauge } from 'lucide-react';

export const AdminNavButtons = ({
  view,
  setView,
  navigate,
  fetchPrecios,
  setIsDossierOpen,
  fetchTemplates,
  fetchSmartPlaylistTemplates,
}) => {
  return (
    <div
      className="flex flex-wrap gap-1 sm:gap-2 mb-2"
      data-testid="admin-nav-buttons"
    >
      <Button
        onClick={() => navigate('/team')}
        {...prefetchHandlers('/team')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'team'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-team-dashboard"
      >
        <UsersRound className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Equipo</span>
      </Button>
      <Button
        onClick={() => navigate('/staff-timing')}
        {...prefetchHandlers('/staff-timing')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'staff-timing'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-staff-timing"
      >
        <UsersRound className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Horarios</span>
      </Button>
      <Button
        onClick={() => navigate('/users')}
        {...prefetchHandlers('/users')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'users'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-users-btn"
      >
        <Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Usuarios</span>
      </Button>
      <Button
        onClick={() => {
          navigate('/precios');
          fetchPrecios();
        }}
        {...prefetchHandlers('/precios')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'precios'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-precios-btn"
      >
        <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Precios</span>
      </Button>
      <Button
        onClick={() => navigate('/finances')}
        {...prefetchHandlers('/finances')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'finanzas'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-finances-btn"
      >
        <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Finanzas</span>
      </Button>
      <Button
        onClick={() => navigate('/presupuestos')}
        {...prefetchHandlers('/presupuestos')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'presupuestos'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-presupuestos-btn"
      >
        <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Presupuestos</span>
      </Button>
      <Button
        onClick={() => setIsDossierOpen(true)}
        variant="outline"
        size="sm"
        className="text-xs sm:text-sm bg-violet-600/80 border-violet-500 text-white hover:bg-violet-700"
        data-testid="nav-dossier-btn"
      >
        <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Dossier</span>
      </Button>
      <Button
        onClick={() => {
          navigate('/plantillas');
          fetchTemplates();
          fetchSmartPlaylistTemplates();
        }}
        {...prefetchHandlers('/plantillas')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'plantillas'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-plantillas-btn"
      >
        <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Plantillas</span>
      </Button>
      <Button
        onClick={() => navigate('/almacenamiento')}
        {...prefetchHandlers('/almacenamiento')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'almacenamiento'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-storage-btn"
        title="Almacenamiento — espacio, archivos y limpieza"
      >
        <HardDrive className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Archivos</span>
      </Button>
      <Button
        onClick={() => navigate('/venues')}
        {...prefetchHandlers('/venues')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'venues'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-venues-btn"
        title="Venues — espacios de cada venue por evento"
      >
        <MapPinned className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Venues</span>
      </Button>
      <Button
        onClick={() => navigate('/wizard-health')}
        {...prefetchHandlers('/wizard-health')}
        variant="outline"
        size="sm"
        className={`text-xs sm:text-sm ${
          view === 'wizard-health'
            ? 'bg-violet-600 border-violet-400'
            : 'bg-violet-600/80 border-violet-500'
        } text-white hover:bg-violet-700`}
        data-testid="nav-wizard-health-btn"
        title="Uso de IA — tokens y coste estimado por función y evento"
      >
        <Gauge className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">IA</span>
      </Button>
      <Button
        onClick={() => navigate('/settings')}
        {...prefetchHandlers('/settings')}
        variant="outline"
        size="sm"
        className="text-xs sm:text-sm bg-violet-600/80 border-violet-500 text-white hover:bg-violet-700"
        data-testid="nav-settings-btn"
      >
        <Settings className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Ajustes</span>
      </Button>
      <Button
        onClick={() => navigate('/admin/audit-log')}
        {...prefetchHandlers('/admin/audit-log')}
        variant="outline"
        size="sm"
        className="text-xs sm:text-sm bg-violet-600/80 border-violet-500 text-white hover:bg-violet-700"
        data-testid="nav-audit-log-btn"
        title="Audit Log — trazabilidad de acciones del equipo"
      >
        <ShieldAlert className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
        <span className="hidden sm:inline">Audit</span>
      </Button>
    </div>
  );
};

export default AdminNavButtons;
